import type { InvoiceRow } from "@/types/api";
import { formatDateTimeIsoLocal } from "@/utils/format/date";

export function formatMoney(amountMinor: number, currency: string): string {
  const code = (currency || "usd").toUpperCase();
  const major = amountMinor / 100;
  const zeroDecimal = code === "JPY" || code === "KRW" || code === "VND";
  const fractionDigits = zeroDecimal ? 0 : 2;
  const baseOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency: code,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  };

  try {
    const narrow = new Intl.NumberFormat("en-US", {
      ...baseOptions,
      currencyDisplay: "narrowSymbol",
    }).format(major);
    const symbol = new Intl.NumberFormat("en-US", {
      ...baseOptions,
      currencyDisplay: "symbol",
    }).format(major);

    // HK$, CN¥, CA$ … — region prefix + symbol reads as two currency marks.
    if (symbol !== narrow && symbol.length > narrow.length) {
      return new Intl.NumberFormat("en-US", {
        ...baseOptions,
        currencyDisplay: "code",
      }).format(major);
    }

    return symbol;
  } catch {
    return `${code} ${major.toFixed(fractionDigits)}`;
  }
}

export function formatInvoiceDate(epochSeconds: number): string {
  const d = new Date(epochSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatInvoiceDateTime(epochSeconds: number): string {
  return formatDateTimeIsoLocal(epochSeconds * 1000);
}

export function formatInvoiceSubtitle(row: InvoiceRow): string {
  const idHint = row.invoice_number ? row.id : "";
  const amount = formatMoney(row.amount_due, row.currency);
  return idHint ? `${amount} · ${idHint}` : amount;
}
